import { useEffect, useState } from 'react';
import { CAMPAIGN_TYPE_LABELS, CAMPAIGN_TYPE_ORDER } from '../constants/campaignTypes';
import { ArrowClockwise, Info, MagnifyingGlass, Package, Storefront, TrendDown, TrendUp } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import api from '../services/api';
import { CreateLogger } from '../services/logger';
import { FormatIsoDate } from '../utils/date';
import { FormatPrice } from '../utils/format';

const logger = CreateLogger('pages/promotions-items');

const PromotionsItems = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');

    const loadItems = async () => {
        try {
            setLoading(true);
            setError(null);

            // Buscar todas as campanhas
            const campaignsRes = await api.get('/promotions');

            if (campaignsRes.success === false) {
                throw new Error(campaignsRes.error || 'Erro ao carregar campanhas');
            }

            const campaigns = campaignsRes.campaigns || [];

            // Coletar todos os itens Anicos primeiro
            const allItemsRaw = [];
            const itemMap = new Map(); // Para evitar duplicatas

            for (const campaign of campaigns) {
                try {
                    const itemsRes = await api.getPromotionItems(campaign.id, campaign.type);

                    if (itemsRes.success && itemsRes.items) {
                        for (const item of itemsRes.items) {
                            const key = item.id;
                            if (!itemMap.has(key)) {
                                allItemsRaw.push({
                                    ...item,
                                    campaign: campaign,
                                });
                                itemMap.set(key, true);
                            }
                        }
                    }
                } catch (err) {
                    logger.warn('promotion_campaign_items_fetch_failed', {
                        campaignId: campaign.id,
                        error: {
                            name: err?.name,
                            message: err?.message,
                        },
                    });
                }
            }

            // Buscar detalhes de todos os itens em lote
            const uniqueItemIds = Array.from(itemMap.keys());
            let itemsDetailsMap = {};

            if (uniqueItemIds.length > 0) {
                try {
                    const batchRes = await api.post('/promotions/items/batch-details', {
                        item_ids: uniqueItemIds,
                    });

                    if (batchRes.success && batchRes.items) {
                        itemsDetailsMap = batchRes.items;
                    }
                } catch (err) {
                    logger.warn('promotion_items_batch_details_failed', {
                        error: {
                            name: err?.name,
                            message: err?.message,
                        },
                    });
                }
            }

            // Combinar dados
            const allItems = allItemsRaw.map((item) => ({
                ...item,
                itemDetails: itemsDetailsMap[item.id] || null,
            }));

            setItems(allItems);
        } catch (err) {
            setError({
                title: 'Erro ao Carregar Itens',
                message: err.message || 'Nao foi possivel carregar os itens das campanhas.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const formatCurrency = (value) => FormatPrice(value);
    const formatDate = (dateString) => FormatIsoDate(dateString, { blank: 'N/A' });
    const getStatusBadge = (status) => <StatusBadge domain="promotion" status={status} />;
    const getTypeBadge = (type) => <StatusBadge domain="promotionType" status={type} />;

    // Calcular se estA ganhando ou perdendo
    const calculateStatus = (item) => {
        const currentPrice = item.price || 0;
        const suggestedPrice = item.suggested_discounted_price || item.min_discounted_price || 0;

        if (currentPrice === 0 || suggestedPrice === 0) {
            return {
                status: 'unknown',
                label: 'Indefinido',
                color: 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300',
            };
        }

        // Se o preAo atual estA acima do sugerido, estA perdendo
        if (currentPrice > suggestedPrice) {
            return {
                status: 'losing',
                label: 'Perdendo',
                color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                icon: TrendDown,
            };
        }

        // Se o preAo atual estA igual ou abaixo do sugerido, estA ganhando
        if (currentPrice <= suggestedPrice) {
            return {
                status: 'winning',
                label: 'Ganhand',
                color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                icon: TrendUp,
            };
        }

        return {
            status: 'unknown',
            label: 'Indefinido',
            color: 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300',
        };
    };

    const filteredItems = items.filter((item) => {
        if (filterStatus !== 'all' && item.status !== filterStatus) {
            return false;
        }
        if (filterType !== 'all' && item.campaign?.type !== filterType) {
            return false;
        }
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const title = (item.itemDetails?.title || '').toLowerCase();
            const sku = (item.itemDetails?.seller_sku || '').toLowerCase();
            const itemId = (item.id || '').toLowerCase();
            const campaignName = (item.campaign?.name || '').toLowerCase();
            return (
                title.includes(searchLower) ||
                sku.includes(searchLower) ||
                itemId.includes(searchLower) ||
                campaignName.includes(searchLower)
            );
        }
        return true;
    });

    if (loading) {
        return (
            <div className="p-6">
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" />
                    <p className="text-mono-600 dark:text-mono-400">Carregando itens das campanhas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <PageHeader
                title="Itens em campanhas"
                subtitle="Visualize todos os itens que participam de campanhas promocionais"
                className="mb-6"
            />

            {error && (
                <div className="mb-6">
                    <ErrorCard
                        title={error.title}
                        message={error.message}
                        type={error.type}
                        onDismiss={() => setError(null)}
                    />
                </div>
            )}

            {/* Filtros */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                    <MagnifyingGlass
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-mono-400"
                        size={20}
                    />
                    <input
                        type="text"
                        placeholder="Buscar por tAtulo, SKU, ID do item ou nome da campanha..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 text-mono-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 text-mono-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">Todos os Status</option>
                    <option value="started">Ativa</option>
                    <option value="pending">Pendente</option>
                    <option value="candidate">Candidata</option>
                </select>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 text-mono-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">Todos os Tipos</option>
                    {CAMPAIGN_TYPE_ORDER.map((type) => (
                        <option key={type} value={type}>
                            {CAMPAIGN_TYPE_LABELS[type]}
                        </option>
                    ))}
                </select>
                <Button onClick={loadItems} variant="secondary" className="flex items-center gap-2">
                    <ArrowClockwise size={18} />
                    Atualizar
                </Button>
            </div>

            {/* Cards */}
            <div className="space-y-4">
                {filteredItems.length === 0 ? (
                    <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                        <p className="text-mono-500 dark:text-mono-400">
                            {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                                ? 'Nenhum item encontrado com os filtros aplicados'
                                : 'Nenhum item encontrado'}
                        </p>
                    </div>
                ) : (
                    filteredItems.map((item, idx) => {
                        const statusInfo = calculateStatus(item);
                        const StatusIcon = statusInfo.icon || Info;
                        const discount =
                            item.original_price && item.price
                                ? ((1 - item.price / item.original_price) * 100).toFixed(1)
                                : null;

                        return (
                            <div
                                key={`${item.campaign?.id}_${item.id}_${idx}`}
                                className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                            >
                                <div className="flex flex-col lg:flex-row">
                                    {/* Imagem do Produto */}
                                    <div className="lg:w-64 p-5 flex-shrink-0 flex flex-col gap-4 border-b lg:border-b-0 lg:border-r border-mono-200 dark:border-mono-800 bg-mono-50/50 dark:bg-mono-900/50">
                                        <div className="relative group aspect-square bg-white dark:bg-mono-800 rounded-xl border border-mono-200 dark:border-mono-700 overflow-hidden flex items-center justify-center">
                                            {item.itemDetails?.thumbnail ? (
                                                <img
                                                    src={item.itemDetails.thumbnail}
                                                    alt={item.itemDetails.title || 'Produto'}
                                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                />
                                            ) : (
                                                <Package size={48} className="text-mono-400" />
                                            )}
                                        </div>
                                        <div className="space-y-2 text-xs font-mono">
                                            {item.id && (
                                                <div className="flex items-center gap-2 text-mono-600 dark:text-mono-400 bg-white dark:bg-mono-900 px-2 py-1.5 rounded border border-mono-200 dark:border-mono-700 shadow-sm truncate">
                                                    <Package size={14} className="text-mono-400" />
                                                    {item.id}
                                                </div>
                                            )}
                                            {item.itemDetails?.seller_sku && (
                                                <div className="flex items-center gap-2 text-mono-600 dark:text-mono-400 bg-white dark:bg-mono-900 px-2 py-1.5 rounded border border-mono-200 dark:border-mono-700 shadow-sm truncate">
                                                    <Storefront size={14} className="text-mono-400" />
                                                    {item.itemDetails.seller_sku}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Informacoes do produto */}
                                    <div className="flex-1 p-5 md:p-6 flex flex-col justify-between">
                                        <div>
                                            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                                                <h2 className="text-lg md:text-xl font-bold text-mono-900 dark:text-white leading-tight">
                                                    {item.itemDetails?.title || item.id || 'Produto sem tAtulo'}
                                                </h2>
                                            </div>
                                            <div className="flex flex-wrap gap-2 mb-5">
                                                {getTypeBadge(item.campaign?.type)}
                                                {getStatusBadge(item.status)}
                                                {item.itemDetails?.available_quantity !== undefined && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-mono-100 dark:bg-mono-800 text-mono-600 dark:text-mono-300 border border-mono-200 dark:border-mono-700">
                                                        <Storefront size={14} />
                                                        Estoque: {item.itemDetails.available_quantity}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="bg-mono-50 dark:bg-mono-900/50 rounded-lg p-4 border border-mono-100 dark:border-mono-800 mb-4">
                                                <div className="flex flex-col sm:flex-row sm:items-end gap-1 sm:gap-4 mb-1">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-mono-500 dark:text-mono-400 font-medium">
                                                            PREAO BASE
                                                        </span>
                                                        <span className="text-sm text-mono-400 dark:text-mono-500 line-through decoration-mono-400">
                                                            {formatCurrency(item.original_price)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-bold tracking-wide uppercase mb-0.5">
                                                            PreAo Campanha
                                                        </span>
                                                        <span className="text-3xl font-bold text-mono-900 dark:text-white tracking-tight">
                                                            {formatCurrency(item.price)}
                                                        </span>
                                                    </div>
                                                </div>
                                                {discount && (
                                                    <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                                                        Desconto: -{discount}%
                                                    </div>
                                                )}
                                            </div>
                                            <div className="group relative inline-block mb-3">
                                                <button className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline decoration-2 underline-offset-2 flex items-center gap-1 text-left">
                                                    {item.campaign?.name || 'Campanha sem nome'}
                                                    <Info size={16} />
                                                </button>
                                                <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 p-3 bg-mono-900 dark:bg-mono-800 text-white text-xs rounded shadow-lg z-10 pointer-events-none">
                                                    <p className="font-bold mb-1">Detalhes da Campanha</p>
                                                    <ul className="list-disc pl-3 space-y-0.5 opacity-90">
                                                        {item.meli_percentage && (
                                                            <li>Rebate ML: {item.meli_percentage}%</li>
                                                        )}
                                                        {item.seller_percentage && (
                                                            <li>Rebate Seller: {item.seller_percentage}%</li>
                                                        )}
                                                        {item.campaign?.finish_date && (
                                                            <li>
                                                                DuraAAo: atA {formatDate(item.campaign.finish_date)}
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status e PreAos */}
                                    <div className="lg:w-64 p-5 md:p-6 border-t lg:border-t-0 lg:border-l border-mono-200 dark:border-mono-800 bg-white dark:bg-mono-900 lg:bg-mono-50/30 lg:dark:bg-mono-800/30 flex flex-col gap-5">
                                        <div>
                                            <span className="text-xs text-mono-400 dark:text-mono-500 uppercase font-bold tracking-wider mb-2 block">
                                                Status Atual
                                            </span>
                                            <div
                                                className={`inline-flex items-center w-full justify-center px-4 py-2 rounded-lg ${statusInfo.color} text-sm font-bold border shadow-sm`}
                                            >
                                                <StatusIcon size={18} className="mr-1.5" />
                                                {statusInfo.label}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-semibold text-mono-500 dark:text-mono-400 uppercase tracking-wide">
                                                    Price to Win
                                                </span>
                                            </div>
                                            <div className="text-xl font-bold text-green-600 dark:text-green-400">
                                                {formatCurrency(
                                                    item.suggested_discounted_price || item.min_discounted_price || 0,
                                                )}
                                            </div>
                                            <div className="text-[11px] text-mono-400 leading-tight">
                                                SugestAo para retomar BuyBox
                                            </div>
                                        </div>
                                        <div className="space-y-1 pt-3 border-t border-mono-200 dark:border-mono-700 border-dashed">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-semibold text-mono-500 dark:text-mono-400 uppercase tracking-wide flex items-center gap-1">
                                                    PreAo MAnimo
                                                </span>
                                            </div>
                                            <div className="text-lg font-semibold text-mono-800 dark:text-mono-200">
                                                {formatCurrency(item.min_discounted_price || 0)}
                                            </div>
                                            <div className="text-[11px] text-red-500 font-medium">
                                                Menor preAo permitido
                                            </div>
                                        </div>
                                        {item.meli_percentage && (
                                            <div className="pt-3 border-t border-mono-200 dark:border-mono-700">
                                                <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 px-3 py-2 rounded-lg border border-purple-100 dark:border-purple-800/30">
                                                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                                                        Rebate ML
                                                    </span>
                                                    <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
                                                        {item.meli_percentage}%
                                                    </span>
                                                </div>
                                                {item.seller_percentage && (
                                                    <div className="mt-2 flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg border border-blue-100 dark:border-blue-800/30">
                                                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                                                            Rebate Seller
                                                        </span>
                                                        <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                                                            {item.seller_percentage}%
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default PromotionsItems;
